import { supabase } from '@/lib/supabase';
import { board } from '@/state/board';
import { openTimers, profile } from '@/state/data';
import { dueLabel, timeShort } from '@/lib/due';
import type { AgentEvent, Proposal } from '@/agent/types';

const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent`;

/** Compact board digest injected into every agent request so simple
 *  questions ("who's overdue?") need zero server tool calls. */
export function boardDigest(): string {
  const now = new Date();
  const lines: string[] = [];
  const entries = board.value;
  const overdue = entries.filter((e) => e.state === 'overdue');
  const dueSoon = entries.filter((e) => e.state === 'due_soon');
  if (overdue.length) {
    lines.push('OVERDUE:');
    for (const e of overdue.slice(0, 12)) {
      lines.push(
        `- ${e.animal.name} (${e.animal.species ?? e.animal.category}${e.animal.kind === 'batch' ? ` ×${e.animal.count}` : ''}, ${e.animal.condition}) — ${e.nextDue ? dueLabel(e.nextDue, now) : ''} [animal_id=${e.animal.id} schedule_id=${e.schedule.id}]`
      );
    }
  }
  if (dueSoon.length) {
    lines.push('DUE SOON:');
    for (const e of dueSoon.slice(0, 12)) {
      lines.push(
        `- ${e.animal.name} — ${e.nextDue ? `due ${timeShort(e.nextDue)}` : ''}${e.schedule.amount != null ? ` · ${e.schedule.amount} ${e.schedule.unit ?? ''} ${e.schedule.food ?? ''}`.trimEnd() : ''} [animal_id=${e.animal.id} schedule_id=${e.schedule.id}]`
      );
    }
  }
  const timers = openTimers.value;
  if (timers.length) {
    lines.push('TIMERS:');
    for (const t of timers.slice(0, 8)) {
      lines.push(`- ${t.label} at ${timeShort(new Date(t.due_at))} [timer_id=${t.id}]`);
    }
  }
  if (!lines.length) lines.push('Board is quiet — nothing overdue or due within the hour.');
  return lines.join('\n');
}

export async function* runAgent(
  history: { role: 'user' | 'assistant'; content: string }[],
  signal?: AbortSignal
): AsyncGenerator<AgentEvent> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    yield { type: 'error', message: 'signed out' };
    return;
  }

  let res: Response;
  try {
    res = await fetch(FN_URL, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        messages: history,
        client_context: {
          now: new Date().toString(),
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          user_name: profile.value?.display_name ?? undefined,
          board_digest: boardDigest(),
        },
      }),
    });
  } catch {
    yield { type: 'error', message: 'offline' };
    return;
  }
  if (!res.ok || !res.body) {
    yield { type: 'error', message: `agent unavailable (${res.status})` };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // parse SSE frames
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let event = 'message';
      let dataStr = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event: ')) event = line.slice(7).trim();
        else if (line.startsWith('data: ')) dataStr += line.slice(6);
      }
      if (!dataStr) continue;
      let payload: unknown;
      try {
        payload = JSON.parse(dataStr);
      } catch {
        continue;
      }
      const p = payload as Record<string, unknown>;
      if (event === 'token') yield { type: 'token', text: String(p.text ?? '') };
      else if (event === 'tool') yield { type: 'tool', name: String(p.name ?? '') };
      else if (event === 'proposal') yield { type: 'proposal', proposal: payload as Proposal };
      else if (event === 'done') yield { type: 'done', text: String(p.text ?? '') };
      else if (event === 'error') yield { type: 'error', message: String(p.message ?? 'agent error') };
    }
  }
}
